import React, { useEffect } from 'react';
import { useRecoilState, useResetRecoilState, useSetRecoilState } from 'recoil';
import { Label, Icon } from '@nooz-optics/nooz-components-lib';
import { IButton, ILabel } from '../../../../../../utils/types/contentful';
import {
  productPackState,
  productPackStepOptionState,
  productState,
  variantState,
} from '../../../../../../utils/recoil/product';
import { Button } from '../../../../../UI/Elements';
import { formatContentfulComponentProps } from '../../../../../../utils/globals';
import { ProductPackOption, ProductPackOptionTrigger } from './productOption';
import { ProductPackGlobalSelector } from './ProductPackGlobalSelector';
import { ProductPackProgressBar } from './productPackProgressBar';
import { BuyProductPackSelectorButton } from '../../../../BuyButton/buyProductSelectorPack';
import { useDrawer } from 'utils/recoil/drawer';

export interface ProductPackOptionSelectorProps {
  label: ILabel;
  subLabel: ILabel;
  button: IButton;
}

export const ProductPackOptionSelector: React.FC<
  ProductPackOptionSelectorProps
> = ({
  button,
  category,
  icon,
  index,
  label,
  modelPicker,
  productMainDetails,
  products,
  subLabel,
}) => {
  const [productPack, setProductPack] = useRecoilState(productPackState);
  const [productPackStepOption, setProductPackStepOption] = useRecoilState(
    productPackStepOptionState
  );
  const drawer = useDrawer();
  const setProduct = useSetRecoilState(productState);
  const setVariant = useSetRecoilState(variantState);
  const resetProduct = useResetRecoilState(productState);
  const resetVariant = useResetRecoilState(variantState);

  const modelPickerData = formatContentfulComponentProps(modelPicker);

  const selectedProduct = productPack[index];
  const isFirstIndex = index === 0;
  const hasMoreThanTwoOptionSelect = productPack.length < 3;

  const handleCloseSlideOverClick = () => {
    resetProduct();
    resetVariant();
    const productPackOptionStep = [...productPackStepOption];
    productPackOptionStep[index] = selectedProduct !== null ? 2 : 1;
    if (productPackOptionStep[index] !== productPackStepOption[index]) {
      setProductPackStepOption(productPackOptionStep);
    }
  };

  const hanldeOptionDelete = () => {
    resetProduct();
    resetVariant();

    const productPackOptionStep = [...productPackStepOption];
    productPackOptionStep[index] = 1;
    setProductPackStepOption(productPackOptionStep);

    const newProductPack = [...productPack];
    // Update product pack at specified index
    newProductPack[index] = null;
    setProductPack(newProductPack);
  };

  useEffect(() => {
    if (drawer.value.show === false) {
      const productPackOptionStep = [...productPackStepOption];
      productPackOptionStep[index] = selectedProduct !== null ? 2 : 1;
      if (productPackOptionStep[index] !== productPackStepOption[index]) {
        setProductPackStepOption(productPackOptionStep);
      }
    }
  }, [drawer.value.show]);

  // Function to handle the click on the option selection
  const handleOnOptionSelectClick = (idx, sp) => {
    drawer.open({
      body: (
        <ProductPackGlobalSelector
          index={idx}
          button={button}
          products={products}
          modelPickerData={modelPickerData}
          category={category}
          productMainDetails={productMainDetails}
          hanldeOptionDelete={hanldeOptionDelete}
        />
      ),
      subHeader: (
        <ProductPackProgressBar
          idx={idx}
          hanldeOptionDelete={hanldeOptionDelete}
        />
      ),
      footer: (
        <div className='w-full p-4 flex justify-center'>
          <BuyProductPackSelectorButton
            index={index}
            buttons={formatContentfulComponentProps(
              modelPickerData.addToCartButtons
            )}
          />
        </div>
      ),
      title: modelPickerData.label,
      placement: 'right',
      width: 'lg:w-screen/2 w-screen-95',
      onClose: () => handleCloseSlideOverClick(sp),
    });
  };

  // Function to handle the click on the product edit selection
  const handleOnOptionEditClick = (idx, sp) => {
    setProduct(selectedProduct.product);
    setVariant(selectedProduct.variant);

    drawer.open({
      body: (
        <ProductPackGlobalSelector
          index={idx}
          button={button}
          products={products}
          modelPickerData={modelPickerData}
          productMainDetails={productMainDetails}
          category={category}
          hanldeOptionDelete={hanldeOptionDelete}
        />
      ),
      subHeader: (
        <ProductPackProgressBar
          idx={idx}
          hanldeOptionDelete={hanldeOptionDelete}
        />
      ),
      title: modelPickerData.label,
      placement: 'right',
      width: 'lg:w-screen/2 w-screen-95',
      footer: (
        <div className='w-full p-4 flex justify-center'>
          <BuyProductPackSelectorButton
            index={index}
            buttons={formatContentfulComponentProps(
              modelPickerData.addToCartButtons
            )}
          />
        </div>
      ),
      onClose: () => handleCloseSlideOverClick(sp),
    });
  };

  return (
    <div
      className={`w-full flex-1 h-96 border lg:mt-1 ${
        !isFirstIndex ? 'lg:ml-4 mt-5' : ''
      } ${selectedProduct ? 'border-solid' : 'border-dashed'}`}>
      <div className='flex flex-col h-full'>
        {/* Header */}
        <div
          className={`flex p-4 border-b ${
            selectedProduct ? 'border-solid' : 'border-dashed'
          }`}>
          {icon && hasMoreThanTwoOptionSelect && (
            <div className='w-16'>
              <Icon
                {...icon}
                size='base'
                width='20px'
              />
            </div>
          )}
          <div className='mx-4 flex-1'>
            <Label
              {...label}
              weight='medium'
              size='lg'
            />
            {subLabel && <Label {...subLabel} />}
          </div>
          {selectedProduct && (
            <div className='flex justify-center items-center'>
              <Button
                onClick={hanldeOptionDelete}
                color={{ classname: 'gray-600' }}
                type='outlined'
                icon={{
                  iconKey: 'IoMdTrash',
                  size: 'lg',
                  color: 'primary',
                }}
              />
            </div>
          )}
        </div>
        {/* Body */}
        <div className='flex-1 flex items-center justify-center'>
          {!selectedProduct ? (
            <ProductPackOptionTrigger
              category={category}
              button={button}
              onClick={() => handleOnOptionSelectClick(index, selectedProduct)}
              index={index}
            />
          ) : (
            <ProductPackOption
              product={selectedProduct}
              category={category}
              onClick={() => handleOnOptionEditClick(index, selectedProduct)}
            />
          )}
        </div>
      </div>
    </div>
  );
};
